import { useMemo } from 'react';
import { PAGE_TYPE } from '../types/pages.type';
import { getPercentageByTotal } from '../utilis/common';
import useAICalculationContext from './useAICalculationContext';
import useAICardDataContext from './useAICardDataContext';
import useAIFacilityContext from './useAIFacilityContext';
import useAIChartAndPatientContext from './useAIChartAndPatientContext';
import useAIAdmissionChatContext from './useAIAdmissionChatContext';
import { useAICommunityTransferChatContext } from './useAICommunityTransferChatContext';
import { useAIDeceasedChatContext } from './useAIDeceasedChatContext';
import { useAIOverallChatContext } from './useAIOverallChatContext';
import { generateChartAnalysis } from '../utilis/chart-analytics';

/**
 * Main custom hook for AI Chat Context
 * Combines all sub-hooks to provide comprehensive context for AI analysis
 */
export const useAIChatContext = ({
  // Page identification (CRITICAL for correct endpoint and vector store)
  pageType = PAGE_TYPE.HOSPITAL,

  // Core data dependencies
  dbData,
  filterListData,
  patientList,
  activeFacilities,
  facilities,
  dynamicCards,

  // Filter and state dependencies
  filter,
  transferType,
  cardFilter,
  percentageBy,
  lockedTotalBy,
  specialComparisonSet,
  filterTotal,
  percentageAgainst,
  projectionDays,
  lastADTDate,

  // UI state dependencies
  hospitalCards, // For Hospital page
  communityTransferCards, // For Community Transfer page
  deceasedCards, // For Deceased page
  admissionCards, // For Admission page
  overallCards, // For Overall page
  rangesSet,
  isLayoutMode,

  // Function dependencies
  getPriorityNumber,
  lockedTotal,
  lockedByFacility,
  isCensusTotalLocked,
  facilityPercentage,

  // Metadata dependencies
  HOSPITAL_CUSTOM_CARD_DATA, // For Hospital page
  COMMUNITY_CUSTOM_CARD_DATA, // For Community Transfer page
  DECEASED_CUSTOM_CARD_DATA, // For Deceased page
  ADMISSION_CUSTOM_CARD_DATA, // For Admission page
  OVERALL_CUSTOM_CARD_DATA, // For Overall page
  tooltipsInfo,

  // Service dependencies
  aiCensusActionsService,
  aiReportActionsService,
  aiTransferActionsService,
  aiDialogActionsService,
  aiDateSuggestionsService,

  // Chart data dependencies (NEW)
  chartData = null,
  chartAggregationType = 'unknown',
  currentChartFilter = null,

  // Admission-specific parameters (for delegation to useAIAdmissionChatContext)
  lockFilterTotalState = null,
  reversedColorAdmission = false,
  comparingAgainstDifferentFacility = false,

  // Overall-specific parameters (for delegation to useAIOverallChatContext)
  shouldReverseOverallColors = false
}) => {
  // CRITICAL: Determine which cards to use based on page type
  const isHospitalPage = pageType === PAGE_TYPE.HOSPITAL;
  const isCommunityTransferPage = pageType === PAGE_TYPE.COMMUNITY_TRANSFER;
  const isDeceasedPage = pageType === PAGE_TYPE.DECEASED;
  const isAdmissionPage = pageType === PAGE_TYPE.ADMISSION;
  const isOverallPage = pageType === PAGE_TYPE.OVERALL;

  // Only handle Hospital, Community Transfer, and Overall pages here (Admission, Deceased are handled separately)
  const currentCards = isHospitalPage
    ? hospitalCards
    : isCommunityTransferPage
      ? communityTransferCards
      : isOverallPage
        ? overallCards
        : hospitalCards; // fallback

  const currentCardMetadata = isHospitalPage
    ? HOSPITAL_CUSTOM_CARD_DATA
    : isCommunityTransferPage
      ? COMMUNITY_CUSTOM_CARD_DATA
      : isOverallPage
        ? OVERALL_CUSTOM_CARD_DATA
        : HOSPITAL_CUSTOM_CARD_DATA; // fallback

  // Use sub-hooks for different aspects of the context (called unconditionally for React rules)
  const calculationContext = useAICalculationContext({
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst
  });

  const cardDataContext = useAICardDataContext({
    pageType, // CRITICAL: Pass page type to get correct cards
    filterListData,
    filter,
    transferType,
    projectionDays,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    dbData,
    getPriorityNumber,
    dynamicCards,
    patientList,
    HOSPITAL_CUSTOM_CARD_DATA, // For Hospital page
    COMMUNITY_CUSTOM_CARD_DATA // For Community Transfer page
    // ADMISSION_CUSTOM_CARD_DATA handled in useAIAdmissionChatContext
  });

  const facilityContext = useAIFacilityContext({
    activeFacilities,
    patientList,
    facilities,
    dbData,
    percentageBy,
    lockedTotal,
    lockedTotalBy,
    pageType
  });

  const chartAndPatientContext = useAIChartAndPatientContext();

  // DELEGATION: Call page-specific hooks (must be called unconditionally for React rules)

  // Admission page hook
  const admissionContext = useAIAdmissionChatContext({
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    facilities,
    dynamicCards,
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    admissionCards,
    rangesSet,
    isLayoutMode,
    getPriorityNumber,
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    ADMISSION_CUSTOM_CARD_DATA,
    tooltipsInfo,
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    chartData,
    chartAggregationType,
    currentChartFilter,
    lockFilterTotalState,
    reversedColorAdmission,
    comparingAgainstDifferentFacility
  });

  // Community Transfer page hook
  const communityTransferContext = useAICommunityTransferChatContext({
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    facilities,
    dynamicCards,
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    communityTransferCards,
    rangesSet,
    isLayoutMode,
    getPriorityNumber,
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    COMMUNITY_CUSTOM_CARD_DATA,
    tooltipsInfo,
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    chartData,
    chartAggregationType,
    currentChartFilter
  });

  // Deceased page hook
  const deceasedContext = useAIDeceasedChatContext({
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    facilities,
    dynamicCards,
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    deceasedCards,
    rangesSet,
    isLayoutMode,
    getPriorityNumber,
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    DECEASED_CUSTOM_CARD_DATA,
    tooltipsInfo,
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    chartData,
    chartAggregationType,
    currentChartFilter
  });

  // Overall page hook
  const overallContext = useAIOverallChatContext({
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    facilities,
    dynamicCards, // 🆕 FIX: Pass dynamicCards for notes filtering
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,
    overallCards,
    rangesSet,
    isLayoutMode,
    getPriorityNumber,
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,
    OVERALL_CUSTOM_CARD_DATA,
    tooltipsInfo,
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,
    chartData,
    chartAggregationType,
    currentChartFilter,
    shouldReverseOverallColors,
    comparingAgainstDifferentFacility
  });

  const hospitalCommunityContext = useMemo(() => {
    // Calculate enhanced metrics for AI analysis
    const totalTransfers = dbData?.total || 0;
    const plannedTransfers = dbData?.plannedHospitalTransfer || 0;
    const unplannedTransfers = dbData?.unplannedHospitalTransfer || 0;

    // Get active filters count
    const activeFiltersCount = cardFilter ? Object.values(cardFilter).filter(arr => Array.isArray(arr) && arr.length > 0).length : 0;

    // Calculate data availability
    const dataCategories = filterListData ? Object.keys(filterListData).filter(key =>
      Array.isArray(filterListData[key]) && filterListData[key].length > 0
    ) : [];

    // Get the correct percentage base (same logic as cards use)
    const currentPercentageBase = specialComparisonSet ? filterTotal : getPercentageByTotal(dbData);

    // IMPORTANT: Terminology definitions for AI
    const terminology = {
      dc: {
        label: "DC",
        meaning: "Discharge - Patients who were ADMITTED to the hospital",
        description: "These are actual hospital discharges (wasAdmitted: true). When users ask about 'discharge' or 'discharges', they mean DC only, NOT ER.",
        keyTerms: ["discharge", "discharges", "discharged", "admitted", "hospitalization", "within 30 days", "30 days"]
      },
      er: {
        label: "ER",
        meaning: "Emergency Room - Patients who went to ER but were NOT admitted",
        description: "These are ER visits without hospital admission (wasAdmitted: false). These are NOT discharges.",
        keyTerms: ["er", "emergency", "emergency room", "not admitted", "er visit"]
      },
      important: "When users ask about 'discharge' or 'discharges within 30 days', they are referring to DC (admitted patients) only, NOT ER (non-admitted patients)."
    };

    // Percentage Calculation Context (critical for accurate AI responses)
    const percentageContext = {
      currentMethod: percentageBy,
      currentPercentageBase,
      percentageAgainst,
      filterTotal,
      specialComparisonSet,
      isComparingAgainstAvgCensus: filterListData?.isComparingAgainstAvgCensus,
      lockedTotalBy,
      transferType,
      calculation: {
        description: `Percentages calculated using ${percentageBy} method`,
        baseValue: currentPercentageBase,
        explanation: specialComparisonSet
          ? `Using filterTotal (${filterTotal}) as base because specialComparisonSet is true`
          : `Using percentageAgainst (${percentageAgainst}) as base from ${percentageBy} method`
      },
      // Priority Filter System Context (explains percentage base changes)
      priorityFiltering: {
        activePriorityFilters: cardFilter?.priorityData || [],
        totalActivePriorityFilters: cardFilter?.priorityData?.length || 0,
        firstPriorityCard: cardFilter?.priorityData?.[0]?.type || null,
        explanation: `When priority filters are applied, percentage bases change:
          - First priority filter uses mainNumPercentage or totalForPercentage
          - Subsequent filters use filtered patient count as denominator
          - Example: If "New Hospitalizations" filter is applied first, percentages for other cards use New Hospitalizations count (7) as base, not total transfers (9)
          - This is why 5/7 = 71% instead of 5/9 = 56%`,
        scalingLogic: `Percentages are scaled when:
          - isComparingAgainstAvgCensus is true
          - lockedTotalBy === "census"
          - First priority item AND no transfer type filter applied`
      }
    };

    // Census Percentage Settings for AI interaction
    const censusSettings = (() => {
      try {
        const settings = aiCensusActionsService?.getCurrentPercentageSettings();
        return {
          currentMethod: settings?.currentMethod,
          customLabel: settings?.customLabel,
          customPercentages: settings?.customPercentages,
          availableMethods: settings?.availableMethods,
          censusData: settings?.censusData,
          explanations: {
            bedCapacity: aiCensusActionsService?.getPercentageExplanation('Bed Capacity'),
            censusAsOfNow: aiCensusActionsService?.getPercentageExplanation('census as of now'),
            censusAverage: aiCensusActionsService?.getPercentageExplanation('Average census of time frame selected')
          },
          actionSuggestions: aiCensusActionsService?.getAIActionSuggestions()
        };
      } catch (error) {
        console.warn('AI Census Actions Service not initialized:', error);
        return {
          currentMethod: percentageBy,
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Current state
    const currentState = {
      selectedTransferType: transferType,
      activeFiltersCount,
      availableDataCategories: dataCategories,
      selectedCards: currentCards?.length || 0,
      totalCards: currentCards?.length || 0,
      hasComparison: !!rangesSet,
      isLayoutMode,
      facilityInfo: {
        hasMultipleFacilities: activeFacilities?.length > 1,
        facilityCount: activeFacilities?.length || 0,
        activeFacilities: activeFacilities
      }
    };

    // Filter information
    const filters = {
      dateRange: {
        startDate: filter?.startDate,
        endDate: filter?.endDate,
        isCustomRange: filter?.startDate && filter?.endDate
      },
      facilities: {
        active: activeFacilities,
        count: activeFacilities?.length || 0
      },
      cardFilters: cardFilter,
      priorityFilters: cardFilter?.priorityData || []
    };

    // Data structure for AI analysis
    const dataStructure = {
      dbData: {
        available: !!dbData,
        recordCount: totalTransfers,
        hasPlannedData: plannedTransfers > 0,
        hasUnplannedData: unplannedTransfers > 0
      },
      filterListData: {
        available: !!filterListData,
        categories: dataCategories,
        totalCategories: dataCategories.length
      },
      patientList: {
        available: !!patientList?.list,
        count: patientList?.list?.length || 0,
        hasNinetyDaysData: !!(patientList?.ninetyDaysData?.length),
        hasHospitalData: !!(patientList?.hospitalData?.length),
        hasCustomTabs: !!(patientList?.customTabs?.length)
      }
    };

    // Available actions for AI suggestions
    const availableActions = {
      // Transfer Type Actions
      canChangeTransferType: true,
      canShowPlannedTransfers: true,
      canShowUnplannedTransfers: true,
      canShowAllTransfers: true,
      canClearTransferFilter: true,

      // Filter Actions
      canApplyFilters: dataCategories.length > 0,
      canApplyCardFilters: true,
      canApplyMultipleFilters: true,
      canToggleSpecificItems: true,
      canResetFilters: activeFiltersCount > 0,
      canToggleAllFilters: true,
      canClearSpecificFilters: true,

      // Report Generation Actions
      canGenerateReports: totalTransfers > 0,
      canGeneratePdfReports: totalTransfers > 0,
      canGenerateExcelReports: totalTransfers > 0,
      canSetupAutomatedReports: totalTransfers > 0,
      canGenerateCardSpecificReports: dataCategories.length > 0,
      canGenerateFilteredReports: activeFiltersCount > 0,

      // Date and Time Actions
      canChangeDateRange: true,
      canSuggestDateRanges: true,
      canApplyDateFilters: true,

      // Percentage and Census Actions
      canChangePercentageMethod: true,
      canAddCustomPercentage: true,
      canExplainPercentageCalculations: true,
      canChangeCensusMethod: true,
      canLockTotals: true,
      canUnlockTotals: true,

      // Layout and Display Actions
      canToggleCards: currentCards?.length > 0,
      canChangeLayout: true,
      canCompareData: true,
      canAnalyzeData: totalTransfers > 0,

      // Chart and Details Actions
      canViewChartData: totalTransfers > 0,
      canAnalyzeChartTrends: totalTransfers > 0,
      canViewPatientDetails: totalTransfers > 0,
      canAnalyzeResidenceData: totalTransfers > 0,
      canCompareTimePeriods: totalTransfers > 0,
      canViewDailyTrends: totalTransfers > 0,
      canViewWeeklyTrends: totalTransfers > 0,
      canViewMonthlyTrends: totalTransfers > 0,
      canViewYearlyTrends: totalTransfers > 0,

      // Advanced Actions
      canExplainDataRelationships: true,
      canProvideInsights: totalTransfers > 0,
      canSuggestOptimalFilters: dataCategories.length > 0,
      canExplainFilterImpact: activeFiltersCount > 0
    };

    // Report Actions Context
    const reportActions = (() => {
      try {
        const reportStatus = aiReportActionsService?.getCurrentReportStatus();
        const reportOptions = aiReportActionsService?.getAvailableReportOptions();
        return {
          currentStatus: reportStatus,
          availableCards: reportStatus?.availableCards,
          canGenerateReports: reportStatus?.canGenerateReports,
          reportTypes: reportStatus?.reportTypes,
          automatedTypes: reportStatus?.automatedTypes,
          suggestions: aiReportActionsService?.getAIActionSuggestions(),
          options: reportOptions
        };
      } catch (error) {
        console.warn('AI Report Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Transfer Actions Context
    const transferActions = (() => {
      try {
        const transferStatus = aiTransferActionsService?.getCurrentTransferStatus();
        const availableTypes = aiTransferActionsService?.getAvailableTransferTypes();
        return {
          currentStatus: transferStatus,
          availableTypes: Object.keys(availableTypes || {}),
          currentType: transferStatus?.currentType,
          currentLabel: transferStatus?.currentLabel,
          currentCount: transferStatus?.currentCount,
          totalPlanned: transferStatus?.totalPlanned,
          totalUnplanned: transferStatus?.totalUnplanned,
          totalAll: transferStatus?.totalAll,
          percentages: transferStatus?.percentages,
          suggestions: aiTransferActionsService?.getAIActionSuggestions(),
          typeDetails: availableTypes
        };
      } catch (error) {
        console.warn('AI Transfer Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Dialog Actions Context
    const dialogActions = (() => {
      try {
        const dialogStatus = aiDialogActionsService?.getCurrentDialogStatus();
        return {
          available: dialogStatus?.initialized,
          canOpenChartDialogs: dialogStatus?.canOpenChartDialogs,
          canOpenResidenceDialogs: dialogStatus?.canOpenResidenceDialogs,
          canOpenBuildingDialogs: dialogStatus?.canOpenBuildingDialogs,
          availableActions: dialogStatus?.availableActions,
          suggestions: [
            {
              type: 'chart_view',
              description: 'Open chart view for trend analysis',
              triggers: ['chart', 'graph', 'trend', 'over time']
            },
            {
              type: 'residence_list',
              description: 'Show patient/Residents List for detailed breakdown',
              triggers: ['patients', 'list', 'who are', 'breakdown']
            },
            {
              type: 'building_view',
              description: 'Show facility/building breakdown',
              triggers: ['facilities', 'buildings', 'hospitals']
            }
          ]
        };
      } catch (error) {
        console.warn('AI Dialog Actions Service not initialized:', error);
        return {
          available: false,
          error: 'Service not initialized'
        };
      }
    })();

    // Locked Functionality Context
    const lockedFunctionality = {
      currentState: {
        lockedTotal,
        lockedByFacility,
        lockedTotalBy,
        isCensusTotalLocked,
        facilityPercentage
      },
      isLocked: !!lockedTotal || !!lockedByFacility?.length,
      lockType: lockedTotalBy,
      lockDescription: lockedTotalBy === 'census'
        ? 'Percentages locked to census data'
        : lockedTotalBy
          ? `Percentages locked to ${lockedTotalBy} transfers`
          : 'No percentage lock applied',
      canLock: true,
      canUnlock: !!lockedTotal || !!lockedByFacility?.length,
      lockOptions: {
        byCensus: {
          type: 'census',
          description: 'Lock percentages to census data',
          available: true
        },
        byTransferType: {
          type: transferType,
          description: `Lock percentages to ${transferType || 'current'} transfers`,
          available: !!transferType
        },
        byFacility: {
          type: 'facility',
          description: 'Lock percentages by facility',
          available: activeFacilities?.length > 1
        }
      },
      explanation: `Locked functionality allows fixing percentage calculations to specific baselines:
        - Census Lock: Uses census data as percentage base
        - Transfer Type Lock: Uses specific transfer type (planned/unplanned) as base
        - Facility Lock: Calculates percentages per facility
        - Priority Filter Lock: First applied filter becomes the percentage base`
    };

    // Date Filtering Context
    const dateFiltering = {
      currentRange: filter,
      hasDateConstraints: false, // Will be updated with real validation info
      canSuggestDates: true,
      validationInfo: aiDateSuggestionsService?.getCurrentValidationInfo()
    };

    // Filter Logic Explanation (from hospital-common.js)
    const filterLogic = {
      description: 'Hospital page uses complex filter logic from hospital-common.js',
      keyFunctions: {
        updateFilterListData: 'Main filter processing function that applies all card filters',
        priorityFilter: 'Handles priority-based filtering where first filter affects percentage calculations',
        checkOnlyOneFilter: 'Determines if only one filter type is active',
        updateFacilityPercentageTotal: 'Calculates facility-wise percentage totals'
      },
      filterFlow: `1. Apply card filters (hospitalizations, insurance, etc.)
          2. Calculate priority filter impact on percentage base
          3. Update locked totals if applicable
          4. Process facility-wise percentages
          5. Update filter totals and percentage bases
          6. Recalculate all card percentages based on new base`,
      prioritySystem: {
        explanation: `Priority filters change percentage calculation base:
          - First applied filter becomes the denominator for other card percentages
          - Example: If "New Hospitalizations" filter (7 patients) is applied first,
            then Insurance card percentages use 7 as base instead of total transfers (9)
          - This is why filtering affects percentage calculations across all cards`,
        currentPriority: cardFilter?.priorityData?.[0]?.type || null,
        priorityFilters: cardFilter?.priorityData || []
      },
      transferTypeImpact: {
        explanation: `Transfer type selection affects all calculations:
          - Planned: Shows only planned transfers and filters all cards accordingly
          - Unplanned: Shows only unplanned transfers and filters all cards accordingly
          - All: Shows combined data
          - None: Shows all data without transfer type filtering`,
        currentType: transferType,
        impact: transferType ? `Currently filtering to ${transferType} transfers only` : 'No transfer type filter applied'
      }
    };

    // Card tooltip information for AI understanding (Hospital and Community Transfer only)
    const tooltipSource = isHospitalPage
      ? tooltipsInfo?.hospitalPage
      : isCommunityTransferPage
        ? tooltipsInfo?.communityTransferPage
        : tooltipsInfo?.hospitalPage;

    const cardTooltips = {
      currentPage: tooltipSource || {},
      availableCards: Object.keys(tooltipSource || {}),
      cardDescriptions: Object.entries(tooltipSource || {}).map(([cardKey, description]) => ({
        cardKey,
        description,
        cardType: cardKey.toLowerCase().replace(/[^a-z0-9]/g, '')
      }))
    };

    // CRITICAL: Chart Data Analytics for AI (NEW)
    const chartAnalytics = (() => {
      if (!chartData || !Array.isArray(chartData) || chartData.length === 0) {

        return {
          available: false,
          message: 'No chart data available. Chart data will be available when viewing chart visualizations or analyzing trends over time.',
          note: 'User can ask to view chart or trends to access this data'
        };
      }


      const analysis = generateChartAnalysis(chartData, chartAggregationType);

      return {
        available: true,
        aggregationType: chartAggregationType,
        currentFilter: currentChartFilter,
        analysis,

        // CRITICAL: Question-answering guides for AI
        questionAnsweringGuides: {
          'Which week had the highest transfers?': {
            answer: `Use analysis.peak to find the period with highest value`,
            example: `The ${analysis.peak?.label} had the highest transfers with ${analysis.peak?.value} transfers`
          },
          'Are transfers trending up or down?': {
            answer: `Use analysis.trend.direction and analysis.trend.description`,
            example: `Transfers show a ${analysis.trend?.direction} trend. ${analysis.trend?.description}`
          },
          'What happened after the peak?': {
            answer: `Use analysis.postPeakAnalysis for detailed post-peak behavior`,
            example: analysis.postPeakAnalysis?.available
              ? `After the peak, ${analysis.postPeakAnalysis.immediateResponse.description}. ${analysis.postPeakAnalysis.analysis}`
              : 'Peak occurs at the end of the period'
          },
          'Show period changes': {
            answer: `Use analysis.periodOverPeriodChanges for all period-to-period changes`,
            example: `Period-over-period changes available for ${analysis.periodOverPeriodChanges?.length} transitions`
          },
          'What is the average?': {
            answer: `Use analysis.statistics.mean, median, and other stats`,
            example: `Average: ${analysis.statistics?.mean} transfers per ${chartAggregationType}, Median: ${analysis.statistics?.median}`
          },
          'Are there any patterns?': {
            answer: `Use analysis.patterns to identify spikes, drops, and recurring patterns`,
            example: `${analysis.patterns?.description}. Spikes found: ${analysis.patterns?.spikes?.length}, Drops: ${analysis.patterns?.drops?.length}`
          }
        },

        // Quick reference data
        summary: {
          totalDataPoints: chartData.length,
          dateRange: `${analysis.dateRange?.start} to ${analysis.dateRange?.end}`,
          peakValue: analysis.peak?.value,
          peakPeriod: analysis.peak?.label,
          averageValue: analysis.statistics?.mean,
          medianValue: analysis.statistics?.median,
          trendDirection: analysis.trend?.direction,
          trendStrength: analysis.trend?.strength,
          overallChange: analysis.trend?.change,
          overallChangePercentage: analysis.trend?.changePercentage,
          largestIncrease: analysis.largestIncrease
            ? `${analysis.largestIncrease.description}`
            : 'N/A',
          largestDecrease: analysis.largestDecrease
            ? `${analysis.largestDecrease.description}`
            : 'N/A',
          volatility: analysis.summary?.volatility,
          dataQuality: analysis.summary?.dataQuality
        }
      };
    })();

    // Debug patient data availability
    // Raw data for detailed analysis (limited for performance)
    const rawData = {
      dbData: dbData ? {
        total: dbData.total,
        plannedHospitalTransfer: dbData.plannedHospitalTransfer,
        unplannedHospitalTransfer: dbData.unplannedHospitalTransfer,
        // CRITICAL: Include census/occupancy values for percentage context
        censusAverage: dbData.censusAverage,
        bedCapacity: dbData.bedCapacity,
        censusAsOfNow: dbData.censusAsOfNow,
        // CRITICAL: Include ALL raw records for accurate cross-filtering (removing limit for accuracy)
        data: Array.isArray(patientList?.list) ? patientList.list : []
      } : null,
      sampleFilterData: filterListData ? Object.fromEntries(
        Object.entries(filterListData).slice(0, 5).map(([key, value]) => [
          key, Array.isArray(value) ? value.slice(0, 10) : value
        ])
      ) : null,
      // CRITICAL: Patient data with notes for AI analysis
      patientData: (() => {
        if (!patientList?.list || !Array.isArray(patientList.list) || patientList.list.length === 0) {
          return null;
        }

        return {
          totalPatients: patientList.list.length,
          // CRITICAL: Pass ALL fields from patient list (backend already excludes logs, highlighter, etc.)
          // This includes: standard fields + dynamic question fields + all supplementary fields
          patients: patientList.list.map(patient => {
            const { __v, ...patientData } = patient; // Exclude only MongoDB version key
            return {
              ...patientData, // Spread ALL fields from patient (includes dynamic question fields)
              id: patient._id // Ensure id is always available
            };
          })
        };
      })(),
      // CRITICAL: Current dashboard filters for AI cross-filter matching
      // This allows the backend to detect when AI query filters match the dashboard's current state
      // and use precomputed cardData for accurate counts (fixes AI vs dashboard count discrepancy)
      currentFilters: (() => {
        const currentFilters = {};
        const priorityData = cardFilter?.priorityData || [];

        // Map priority filter types to standardized filter keys used by AI
        priorityData.forEach(pf => {
          const filterType = pf.type;
          const selectedValues = pf.selectedValues || [];

          // Map filter types to standardized keys
          const typeMapping = {
            'dxData': 'diagnosis',
            'doctorData': 'doctor',
            'floorsData': 'floor',
            'insuranceData': 'insurance',
            'daysData': 'day_of_week',
            'hospitalData': 'hospital',
            'shiftData': 'shift',
            'nurseData': 'nurse',
            'hospitalizationsData': 'hospitalization_type',
            'returnsData': 'returned',
            'DCERData': 'dc_er'
          };

          const filterKey = typeMapping[filterType] || filterType;

          if (selectedValues.length > 0) {
            // Extract labels from selected values for comparison
            currentFilters[filterKey] = selectedValues.map(v =>
              typeof v === 'object' ? (v.label || v.name || String(v)) : String(v)
            );
          }
        });

        return currentFilters;
      })()
    };

    // CRITICAL: Data Validation Rules for AI
    const dataValidationRules = {
      forExactCounts: {
        rule: "ALWAYS use item.actualValue or item.total for 'how many' questions",
        wrongFields: ["scaledValue", "projectedValue", "scaledPercentage"],
        correctFields: ["actualValue", "total", "value"],
        example: "For 'How many DC by New Hospitalizations?', use dcer.dc.actualValue = 75, NOT scaledValue"
      },
      forPercentages: {
        rule: "Use calculatedPercentage or scaledPercentage for percentage questions",
        correctFields: ["calculatedPercentage", "scaledPercentage", "percentage"]
      },
      forProjections: {
        rule: "Use projectedValue only for projection/forecast questions",
        correctFields: ["projectedValue", "projectedPercentage"]
      },
      commonMistakes: {
        mistake1: "Using scaledValue instead of actualValue for counts",
        mistake2: "Applying rounding to base counts",
        mistake3: "Using projected values for current data questions"
      }
    };

    // CRITICAL: Common User Questions and How AI Should Respond
    const commonQuestions = {
      "How many DC by New Hospitalizations?": {
        answer: "Find DC item in cardData.dcer.itemsWithPercentages where filtered by New Hospitalizations",
        calculation: "Use item.actualValue or item.total - this is the EXACT count, no rounding",
        criticalNote: "If user asks this and you see 75 in UI but calculate 74, you made an error - use exact count"
      },
      "How many discharges within 30 days?": {
        answer: "Look at DC card data (not ER), use item.total values",
        calculation: "Sum DC items, apply date range scaling if needed"
      },
      "Why did percentages change when I filtered?": {
        answer: "Priority filter system changes percentage denominators",
        explanation: "First filter becomes new base for other card percentages"
      },
      "What's the difference between DC and ER?": {
        answer: "DC = admitted patients (wasAdmitted: true), ER = not admitted (wasAdmitted: false)"
      },
      "How are projections calculated?": {
        answer: "Use projectionPerMonth() function with current values and date range",
        formula: "For periods ≤30 days: (projectionDays * total) / actualDays"
      },
      "Why are some percentages different?": {
        answer: "Returns and Hospitals cards use percentageAgainst, others use currentPercentageBase"
      },
      "What trends should I be concerned about?": {
        answer: "Look for patterns that deviate from expected healthcare norms",
        analysis: "Use contextual anomaly detection considering expected patterns, statistical distribution, and domain knowledge"
      },
      "Are there any unusual patterns in the data?": {
        answer: "Analyze each card for deviations from expected healthcare patterns",
        method: "Compare actual distribution against expected patterns using healthcare domain knowledge"
      }
    };

    // Core metrics
    const metrics = {
      totalTransfers,
      plannedTransfers,
      unplannedTransfers,
      plannedPercentage: totalTransfers > 0 ? ((plannedTransfers / totalTransfers) * 100).toFixed(1) : 0,
      unplannedPercentage: totalTransfers > 0 ? ((unplannedTransfers / totalTransfers) * 100).toFixed(1) : 0,
      filterTotal,
      percentageAgainst,
      projectionDays,
      lastADTDate
    };

    // Enhanced context with structured data INCLUDING ALL CALCULATION FUNCTIONS AND BUSINESS LOGIC
    return {
      pageType: pageType, // Use the passed pageType parameter (HOSPITAL, DECEASED, etc.)
      pageData: {
        // CRITICAL: Calculation Functions - AI now has access to actual function source code
        ...calculationContext,

        // CRITICAL: Card Data - AI has access to all card data with percentages
        cardData: cardDataContext.cardData,
        currentPercentageBase: cardDataContext.currentPercentageBase,
        calculateCardPercentages: cardDataContext.calculateCardPercentages,

        // CRITICAL: Facility Analysis - AI can answer multi-facility questions
        ...facilityContext,

        // Active facilities (also expected directly in pageData by backend)
        activeFacilities,

        // CRITICAL: Chart and Patient Details - AI can provide comprehensive analysis
        ...chartAndPatientContext,

        // Core metrics
        metrics,

        // IMPORTANT: Terminology definitions for AI understanding
        terminology,

        // Percentage Calculation Context (critical for accurate AI responses)
        percentageContext,

        // Census Percentage Settings for AI interaction
        censusSettings,

        // Current state
        currentState,

        // Filter information
        filters,

        // Data structure for AI analysis
        dataStructure,

        // Available actions for AI suggestions
        availableActions,

        // Report Actions Context
        reportActions,

        // Transfer Actions Context
        transferActions,

        // Dialog Actions Context
        dialogActions,

        // Locked Functionality Context
        lockedFunctionality,

        // Date Filtering Context
        dateFiltering,

        // Filter Logic Explanation (from hospital-common.js)
        filterLogic,

        // Card tooltip information for AI understanding
        cardTooltips,

        // Raw data for detailed analysis (limited for performance)
        rawData,

        // CRITICAL: Data Validation Rules for AI
        dataValidationRules,

        // CRITICAL: Common User Questions and How AI Should Respond
        commonQuestions,

        // CRITICAL: Card metadata for all cards including custom cards (aiDescription, description, labels)
        cardMetadata: currentCardMetadata,

        // CRITICAL: Custom/Dynamic Cards for action recognition
        customCards: {
          dynamicCards: dynamicCards || [],
          customTabs: patientList?.customTabs || []
        },

        // CRITICAL: Chart Analytics for Trend Analysis (NEW)
        chartAnalytics,

        // CRITICAL: Chart interpretation instructions for AI
        chartInterpretation: {
          description: 'How to answer chart-related questions',
          instructions: [
            '1. Check if chartAnalytics.available is true',
            '2. Use chartAnalytics.analysis for comprehensive data',
            '3. Reference chartAnalytics.questionAnsweringGuides for common questions',
            '4. Provide specific numbers, dates, and percentages from the analysis',
            '5. Always mention the aggregation type (daily, weekly, monthly)',
            '6. For trends, use analysis.trend for direction and strength',
            '7. For peaks, use analysis.peak with specific period and value',
            '8. For changes, use analysis.periodOverPeriodChanges for detailed breakdowns',
            '9. For patterns, use analysis.patterns for spikes, drops, and cycles',
            '10. For post-peak analysis, use analysis.postPeakAnalysis'
          ],
          commonMistakes: [
            'Not specifying which week/period when answering "highest" questions',
            'Forgetting to include percentage changes in comparisons',
            'Not mentioning the context (date range, aggregation type)',
            'Providing vague answers instead of specific numbers',
            'Not explaining what happened after significant events'
          ],
          bestPractices: [
            'Always include specific numbers and percentages',
            'Mention the time period (week of X, month of Y)',
            'Provide context about the overall trend',
            'Compare to previous periods for context',
            'Explain the significance of changes',
            'Use the exact period labels from the data (e.g., "Week of Aug 24-30")'
          ]
        }
      },
      facilityIds: activeFacilities,
      timestamp: new Date().toISOString()
    };
  }, [
    // Page identification (CRITICAL - determines API endpoint and vector store key)
    // Note: isAdmissionPage handled by useAIAdmissionChatContext
    pageType,
    isHospitalPage,
    isCommunityTransferPage,

    // Core data dependencies (used directly in useMemo)
    dbData,
    filterListData,
    patientList,
    activeFacilities,
    dynamicCards,

    // Filter and state dependencies (used directly in useMemo)
    filter,
    transferType,
    cardFilter,
    percentageBy,
    lockedTotalBy,
    specialComparisonSet,
    filterTotal,
    percentageAgainst,
    projectionDays,
    lastADTDate,

    // UI state dependencies (used directly in useMemo)
    currentCards,
    rangesSet,
    isLayoutMode,

    // Function dependencies (used directly in useMemo)
    lockedTotal,
    lockedByFacility,
    isCensusTotalLocked,
    facilityPercentage,

    // Metadata dependencies (used directly in useMemo)
    currentCardMetadata,
    tooltipsInfo,

    // Service dependencies (used directly in useMemo)
    aiCensusActionsService,
    aiReportActionsService,
    aiTransferActionsService,
    aiDialogActionsService,
    aiDateSuggestionsService,

    // Sub-hook dependencies (results from sub-hooks used in useMemo)
    calculationContext,
    cardDataContext.calculateCardPercentages,
    cardDataContext.cardData,
    cardDataContext.currentPercentageBase,
    chartAndPatientContext,
    facilityContext,

    // Chart data dependencies (NEW)
    chartData,
    chartAggregationType,
    currentChartFilter
  ]);

  // Return the appropriate context based on page type
  if (isAdmissionPage) {
    return admissionContext;
  }

  if (isCommunityTransferPage) {
    return communityTransferContext;
  }

  if (isDeceasedPage) {
    return deceasedContext;
  }

  if (isOverallPage) {
    return overallContext;
  }

  // Default: Hospital page (or fallback)
  return hospitalCommunityContext;
};

export default useAIChatContext;
